import { useState, useRef, useEffect } from 'react';
import { Search, MapPin, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ServiceType } from '@/types';
import { SERVICE_TYPE_LABELS } from '@/types';

interface SearchBarProps {
  initialLocation?: string;
  initialServiceType?: ServiceType;
  onSearch?: (location: string, serviceType?: ServiceType) => void;
  variant?: 'default' | 'hero';
}

export function SearchBar({
  initialLocation = '',
  initialServiceType,
  onSearch,
  variant = 'default',
}: SearchBarProps) {
  const navigate = useNavigate();
  const [location, setLocation] = useState(initialLocation);
  const [serviceType, setServiceType] = useState<ServiceType | undefined>(initialServiceType);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowServiceDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(location, serviceType);
    } else {
      const params = new URLSearchParams();
      if (location) params.set('loc', location);
      if (serviceType) params.set('type', serviceType);
      navigate(`/search?${params.toString()}`);
    }
  };

  const isHero = variant === 'hero';

  return (
    <form onSubmit={handleSubmit} className={`
      flex flex-col sm:flex-row gap-2 sm:gap-0
      ${isHero ? 'bg-white rounded-2xl shadow-xl p-2' : 'bg-white rounded-lg shadow-sm border border-gray-200'}
    `}>
      {/* Location Input */}
      <div className={`
        flex items-center flex-1 gap-2 px-4
        ${isHero ? 'py-3' : 'py-2'}
        ${!isHero && 'border-b sm:border-b-0 sm:border-r border-gray-200'}
      `}>
        <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0" />
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="City, state, or zip code"
          className={`
            flex-1 bg-transparent border-none focus:outline-none focus:ring-0
            ${isHero ? 'text-lg' : 'text-sm'}
          `}
        />
        {location && (
          <button
            type="button"
            onClick={() => setLocation('')}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Service Type Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setShowServiceDropdown(!showServiceDropdown)}
          className={`
            flex items-center gap-2 px-4 w-full sm:w-auto text-left
            ${isHero ? 'py-3' : 'py-2'}
            ${isHero ? 'text-lg' : 'text-sm'}
            ${serviceType ? 'text-gray-900' : 'text-gray-500'}
          `}
        >
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <span className="flex-1">
            {serviceType ? SERVICE_TYPE_LABELS[serviceType] : 'All services'}
          </span>
        </button>

        {showServiceDropdown && (
          <div className="absolute top-full left-0 right-0 sm:left-auto sm:right-0 mt-2 w-full sm:w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
            <button
              type="button"
              onClick={() => {
                setServiceType(undefined);
                setShowServiceDropdown(false);
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
            >
              All services
            </button>
            {(Object.keys(SERVICE_TYPE_LABELS) as ServiceType[]).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setServiceType(type);
                  setShowServiceDropdown(false);
                }}
                className={`
                  w-full px-4 py-2 text-left text-sm hover:bg-gray-50
                  ${serviceType === type ? 'bg-primary-50 text-primary-700' : ''}
                `}
              >
                {SERVICE_TYPE_LABELS[type]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        className={`
          btn btn-primary flex items-center justify-center gap-2
          ${isHero ? 'px-8 py-3 text-lg rounded-xl' : 'px-4 py-2 rounded-lg sm:rounded-l-none'}
        `}
      >
        <Search className="w-5 h-5" />
        <span>Search</span>
      </button>
    </form>
  );
}
